import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles, getCategoryName } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到"
          description="抱歉，您访问的文章不存在。"
          keywords="91免费版,文章"
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-file-search-line text-2xl" />
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug);
  const categoryName = getCategoryName(article.categorySlug);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '文章', path: '/article/getting-started' },
    { label: article.title },
  ];

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91free.com' },
        { '@type': 'ListItem', position: 2, name: '文章', item: 'https://www.91free.com/article/getting-started' },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.91free.com/article/${article.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Organization', name: article.author },
      datePublished: article.publishDate,
      image: article.image,
      publisher: {
        '@type': 'Organization',
        name: '91免费版',
        url: 'https://www.91free.com',
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.91free.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.image}
        ogType="article"
        schemaData={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 bg-background-50 min-h-screen">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4 bg-white rounded-lg border border-background-200/70 overflow-hidden">
            <div className="relative h-64 sm:h-80 w-full overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 text-xs rounded-full bg-background-100 text-foreground-500"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  阅读 {article.readTime}
                </span>
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:text-xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group rounded-lg bg-white border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 w-full overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{related.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}